import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../lib/api'
import DesignSupportLayout from './designSupport/DesignSupportLayout'
import MenuGuideButton from '../components/MenuGuideButton'

function downloadBlob(data, filename) {
  const blob = new Blob([data], { type: 'text/csv;charset=utf-8' })
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  window.URL.revokeObjectURL(url)
}

async function downloadFromApi(path, filename) {
  const res = await api.get(path, { responseType: 'blob' })
  downloadBlob(res.data, filename)
}

export default function DesignSupportDataAdminPage() {
  const [tab, setTab] = useState('kcd')

  const [kcdQ, setKcdQ] = useState('')
  const [kcdPage, setKcdPage] = useState(1)
  const [kcdRows, setKcdRows] = useState([])
  const [kcdTotal, setKcdTotal] = useState(0)
  const [kcdLoading, setKcdLoading] = useState(false)

  const [riderQ, setRiderQ] = useState('')
  const [riderPage, setRiderPage] = useState(1)
  const [riderRows, setRiderRows] = useState([])
  const [riderTotal, setRiderTotal] = useState(0)
  const [riderLoading, setRiderLoading] = useState(false)

  const [mapKcd, setMapKcd] = useState('')
  const [mapQ, setMapQ] = useState('')
  const [mapPage, setMapPage] = useState(1)
  const [mapRows, setMapRows] = useState([])
  const [mapTotal, setMapTotal] = useState(0)
  const [mapLoading, setMapLoading] = useState(false)

  const [sgQ, setSgQ] = useState('')
  const [sgPage, setSgPage] = useState(1)
  const [sgRows, setSgRows] = useState([])
  const [sgTotal, setSgTotal] = useState(0)
  const [sgLoading, setSgLoading] = useState(false)

  const [stInsurerCode, setStInsurerCode] = useState('')
  const [stProductCode, setStProductCode] = useState('')
  const [stTierSystem, setStTierSystem] = useState('')
  const [stQ, setStQ] = useState('')
  const [stPage, setStPage] = useState(1)
  const [stRows, setStRows] = useState([])
  const [stTotal, setStTotal] = useState(0)
  const [stLoading, setStLoading] = useState(false)

  const [mdInsurerCode, setMdInsurerCode] = useState('')
  const [mdProductCode, setMdProductCode] = useState('')
  const [mdNLabel, setMdNLabel] = useState('')
  const [mdQ, setMdQ] = useState('')
  const [mdPage, setMdPage] = useState(1)
  const [mdRows, setMdRows] = useState([])
  const [mdTotal, setMdTotal] = useState(0)
  const [mdLoading, setMdLoading] = useState(false)

  const [msg, setMsg] = useState('')

  const kcdQRef = useRef('')
  const riderQRef = useRef('')
  const mapKcdRef = useRef('')
  const mapQRef = useRef('')
  const sgQRef = useRef('')
  useEffect(() => {
    kcdQRef.current = kcdQ
  }, [kcdQ])
  useEffect(() => {
    riderQRef.current = riderQ
  }, [riderQ])
  useEffect(() => {
    mapKcdRef.current = mapKcd
    mapQRef.current = mapQ
  }, [mapKcd, mapQ])
  useEffect(() => {
    sgQRef.current = sgQ
  }, [sgQ])

  const loadKcd = useCallback(() => {
    setKcdLoading(true)
    api
      .get('/operator/design-support/admin/kcd-catalog', { params: { q: kcdQRef.current.trim(), page: kcdPage, size: 50 } })
      .then((res) => {
        const d = res?.data?.data
        setKcdRows(Array.isArray(d?.items) ? d.items : [])
        setKcdTotal(Number(d?.total) || 0)
      })
      .catch(() => setKcdRows([]))
      .finally(() => setKcdLoading(false))
  }, [kcdPage])

  const loadRider = useCallback(() => {
    setRiderLoading(true)
    api
      .get('/operator/design-support/admin/rider-types', { params: { q: riderQRef.current.trim(), page: riderPage, size: 50 } })
      .then((res) => {
        const d = res?.data?.data
        setRiderRows(Array.isArray(d?.items) ? d.items : [])
        setRiderTotal(Number(d?.total) || 0)
      })
      .catch(() => setRiderRows([]))
      .finally(() => setRiderLoading(false))
  }, [riderPage])

  const loadMap = useCallback(() => {
    setMapLoading(true)
    api
      .get('/operator/design-support/admin/kcd-rider-mappings', {
        params: { kcdCategory: mapKcdRef.current.trim(), q: mapQRef.current.trim(), page: mapPage, size: 50 },
      })
      .then((res) => {
        const d = res?.data?.data
        setMapRows(Array.isArray(d?.items) ? d.items : [])
        setMapTotal(Number(d?.total) || 0)
      })
      .catch(() => setMapRows([]))
      .finally(() => setMapLoading(false))
  }, [mapPage])

  const loadSg = useCallback(() => {
    setSgLoading(true)
    api
      .get('/operator/design-support/admin/surgery-grades', { params: { q: sgQRef.current.trim(), page: sgPage, size: 50 } })
      .then((res) => {
        const d = res?.data?.data
        setSgRows(Array.isArray(d?.items) ? d.items : [])
        setSgTotal(Number(d?.total) || 0)
      })
      .catch(() => setSgRows([]))
      .finally(() => setSgLoading(false))
  }, [sgPage])

  const loadTierSnapshot = useCallback(() => {
    setStLoading(true)
    api
      .get('/operator/design-support/admin/surgery-tier-snapshots', {
        params: {
          insurerCode: stInsurerCode.trim(),
          productCode: stProductCode.trim(),
          tierSystem: stTierSystem.trim(),
          q: stQ.trim(),
          page: stPage,
          size: 50,
        },
      })
      .then((res) => {
        const d = res?.data?.data
        setStRows(Array.isArray(d?.items) ? d.items : [])
        setStTotal(Number(d?.total) || 0)
      })
      .catch(() => setStRows([]))
      .finally(() => setStLoading(false))
  }, [stInsurerCode, stProductCode, stTierSystem, stQ, stPage])

  const loadMajorN = useCallback(() => {
    setMdLoading(true)
    api
      .get('/operator/design-support/admin/major-disease-n', {
        params: {
          insurerCode: mdInsurerCode.trim(),
          productCode: mdProductCode.trim(),
          nLabel: mdNLabel.trim(),
          q: mdQ.trim(),
          page: mdPage,
          size: 50,
        },
      })
      .then((res) => {
        const d = res?.data?.data
        setMdRows(Array.isArray(d?.items) ? d.items : [])
        setMdTotal(Number(d?.total) || 0)
      })
      .catch(() => setMdRows([]))
      .finally(() => setMdLoading(false))
  }, [mdInsurerCode, mdProductCode, mdNLabel, mdQ, mdPage])

  useEffect(() => {
    if (tab === 'kcd') loadKcd()
  }, [tab, kcdPage, loadKcd])

  useEffect(() => {
    if (tab === 'rider') {
      loadRider()
      loadMap()
    }
  }, [tab, riderPage, mapPage, loadRider, loadMap])

  useEffect(() => {
    if (tab === 'surgery') loadSg()
  }, [tab, sgPage, loadSg])

  useEffect(() => {
    if (tab === 'tierSnapshot') loadTierSnapshot()
  }, [tab, loadTierSnapshot])

  useEffect(() => {
    if (tab === 'majorN') loadMajorN()
  }, [tab, loadMajorN])

  const upload = async (path, file, inputEl) => {
    if (!file) return
    const fd = new FormData()
    fd.append('file', file)
    setMsg('')
    try {
      const res = await api.post(path, fd)
      const d = res?.data?.data
      if (d && typeof d.imported === 'number') {
        let m = `가져오기 완료: ${d.imported}건 반영`
        if (typeof d.skipped === 'number' && d.skipped > 0) m += ` · 건너뜀 ${d.skipped}건`
        if (Array.isArray(d.errors) && d.errors.length > 0) m += ` · 참고: ${d.errors.slice(0, 5).join('; ')}`
        setMsg(m)
      } else {
        setMsg(`가져오기 완료: ${JSON.stringify(d)}`)
      }
      if (tab === 'kcd') loadKcd()
      if (tab === 'rider') {
        loadRider()
        loadMap()
      }
      if (tab === 'surgery') loadSg()
      if (tab === 'tierSnapshot') loadTierSnapshot()
      if (tab === 'majorN') loadMajorN()
    } catch (e) {
      setMsg('업로드에 실패했습니다.')
    }
    if (inputEl) inputEl.value = ''
  }

  const kcdPages = Math.max(1, Math.ceil(kcdTotal / 50))
  const riderPages = Math.max(1, Math.ceil(riderTotal / 50))
  const mapPages = Math.max(1, Math.ceil(mapTotal / 50))
  const sgPages = Math.max(1, Math.ceil(sgTotal / 50))
  const stPages = Math.max(1, Math.ceil(stTotal / 50))
  const mdPages = Math.max(1, Math.ceil(mdTotal / 50))

  return (
    <DesignSupportLayout>
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-semibold text-slate-900">질병·특약·수술명 데이터 관리</h2>
            <MenuGuideButton slug="data-management" title="데이터관리 가이드" />
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 text-sm">
          <Link className="text-brand-sky hover:underline" to="/design/disease-code">
            질병코드·특약 조회 →
          </Link>
          <Link className="text-brand-sky hover:underline" to="/design/surgery-search">
            수술명·종 조회 →
          </Link>
        </div>
      </div>

      {msg && <p className="mt-3 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800">{msg}</p>}

      <div className="mt-6 flex flex-wrap gap-2 border-b border-slate-200 pb-2 text-sm">
        {[
          { id: 'kcd', label: '질병코드 관리' },
          { id: 'rider', label: '특약 매핑 관리' },
          { id: 'surgery', label: '수술명 관리' },
          { id: 'tierSnapshot', label: '수술 스냅샷(원수사·상품)' },
          { id: 'majorN', label: 'N대 질병(원수사·상품)' },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            className={
              tab === t.id ? 'font-semibold text-brand-navy border-b-2 border-brand-sky -mb-2.5 px-2' : 'text-slate-600 px-2 hover:text-slate-900'
            }
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'kcd' && (
        <div className="mt-6 space-y-4">
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-[12rem] flex-1">
              <label className="text-xs font-medium text-slate-600">검색 (코드·한글명·영문)</label>
              <input
                className="mt-0.5 w-full rounded border border-slate-300 px-3 py-2 text-sm"
                value={kcdQ}
                onChange={(e) => setKcdQ(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    setKcdPage(1)
                    setTimeout(() => loadKcd(), 0)
                  }
                }}
              />
            </div>
            <button
              type="button"
              className="rounded bg-brand-navy px-4 py-2 text-sm text-white hover:bg-slate-800"
              onClick={() => {
                setKcdPage(1)
                setTimeout(() => loadKcd(), 0)
              }}
            >
              검색
            </button>
            <button type="button" className="rounded border border-slate-300 px-3 py-2 text-sm hover:bg-slate-50" onClick={() => downloadFromApi('/operator/design-support/admin/kcd-catalog/template', 'kcd_catalog_template.csv')}>
              템플릿
            </button>
            <button type="button" className="rounded border border-slate-300 px-3 py-2 text-sm hover:bg-slate-50" onClick={() => downloadFromApi('/operator/design-support/admin/kcd-catalog/export', 'kcd_catalog.csv')}>
             CSV 내려받기
            </button>
            <label className="rounded border border-slate-300 px-3 py-2 text-sm hover:bg-slate-50 cursor-pointer">
              가져오기
              <input
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => upload('/operator/design-support/admin/kcd-catalog/import', e.target.files?.[0], e.target)}
              />
            </label>
          </div>
          <p className="text-xs text-slate-500">전체 {kcdTotal.toLocaleString()}건 · 페이지 {kcdPage} / {kcdPages}</p>
          <div className="overflow-x-auto rounded border border-slate-200">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs font-semibold text-slate-600">
                <tr>
                  <th className="px-3 py-2">코드</th>
                  <th className="px-3 py-2">한글명</th>
                  <th className="px-3 py-2">영문명</th>
                  <th className="px-3 py-2">비고</th>
                </tr>
              </thead>
              <tbody>
                {kcdLoading ? (
                  <tr>
                    <td colSpan={4} className="px-3 py-8 text-center text-slate-500">
                      불러오는 중…
                    </td>
                  </tr>
                ) : kcdRows.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-3 py-8 text-center text-slate-500">
                      데이터가 없습니다. 템플릿을 받아 CSV를 채운 뒤 가져오기 하세요.
                    </td>
                  </tr>
                ) : (
                  kcdRows.map((r) => (
                    <tr key={r.id} className="border-t border-slate-100">
                      <td className="px-3 py-2 font-mono text-xs">{r.code}</td>
                      <td className="px-3 py-2">{r.nameKo}</td>
                      <td className="px-3 py-2 text-xs text-slate-600">{r.nameEn || '—'}</td>
                      <td className="px-3 py-2 text-xs text-slate-600">{r.notes || '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {kcdPages > 1 && (
            <div className="flex gap-2 text-sm">
              <button type="button" className="rounded border px-2 py-1 disabled:opacity-40" disabled={kcdPage <= 1} onClick={() => setKcdPage((p) => p - 1)}>
                이전
              </button>
              <button type="button" className="rounded border px-2 py-1 disabled:opacity-40" disabled={kcdPage >= kcdPages} onClick={() => setKcdPage((p) => p + 1)}>
                다음
              </button>
            </div>
          )}
        </div>
      )}

      {tab === 'rider' && (
        <div className="mt-6 space-y-10">
          <section>
            <h2 className="text-lg font-semibold text-slate-900">특약 유형</h2>
            <div className="mt-3 flex flex-wrap items-end gap-2">
              <input
                className="min-w-[12rem] flex-1 rounded border border-slate-300 px-3 py-2 text-sm"
                placeholder="코드·이름 검색"
                value={riderQ}
                onChange={(e) => setRiderQ(e.target.value)}
              />
              <button
                type="button"
                className="rounded bg-brand-navy px-4 py-2 text-sm text-white"
                onClick={() => {
                  setRiderPage(1)
                  setTimeout(() => loadRider(), 0)
                }}
              >
                검색
              </button>
              <button type="button" className="rounded border px-3 py-2 text-sm" onClick={() => downloadFromApi('/operator/design-support/admin/rider-types/template', 'rider_types_template.csv')}>
                템플릿
              </button>
              <button type="button" className="rounded border px-3 py-2 text-sm" onClick={() => downloadFromApi('/operator/design-support/admin/rider-types/export', 'rider_types.csv')}>
               CSV 내려받기
              </button>
              <label className="rounded border px-3 py-2 text-sm cursor-pointer">
                가져오기
                <input type="file" accept=".csv" className="hidden" onChange={(e) => upload('/operator/design-support/admin/rider-types/import', e.target.files?.[0], e.target)} />
              </label>
            </div>
            <p className="mt-1 text-xs text-slate-500">전체 {riderTotal.toLocaleString()}건</p>
            <div className="mt-2 overflow-x-auto rounded border">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-xs font-semibold text-slate-600">
                  <tr>
                    <th className="px-3 py-2">코드</th>
                    <th className="px-3 py-2">이름</th>
                    <th className="px-3 py-2">보장요지</th>
                    <th className="px-3 py-2">순서</th>
                  </tr>
                </thead>
                <tbody>
                  {riderLoading ? (
                    <tr>
                      <td colSpan={4} className="py-6 text-center text-slate-500">
                        불러오는 중…
                      </td>
                    </tr>
                  ) : riderRows.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="py-8 text-center text-slate-500">
                        데이터가 없습니다. 템플릿으로 CSV를 만든 뒤 가져오기 하세요.
                      </td>
                    </tr>
                  ) : (
                    riderRows.map((r) => (
                      <tr key={r.id} className="border-t">
                        <td className="px-3 py-2 font-mono text-xs">{r.code}</td>
                        <td className="px-3 py-2">{r.name}</td>
                        <td className="px-3 py-2 text-xs">{r.coverageSummary || '—'}</td>
                        <td className="px-3 py-2">{r.sortOrder}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {riderPages > 1 && (
              <div className="mt-3 flex gap-2 text-sm">
                <button type="button" className="rounded border px-2 py-1 disabled:opacity-40" disabled={riderPage <= 1} onClick={() => setRiderPage((p) => p - 1)}>
                  이전
                </button>
                <span className="self-center text-xs text-slate-600">
                  {riderPage} / {riderPages}
                </span>
                <button type="button" className="rounded border px-2 py-1 disabled:opacity-40" disabled={riderPage >= riderPages} onClick={() => setRiderPage((p) => p + 1)}>
                  다음
                </button>
              </div>
            )}
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900">KCD ↔ 특약 매핑</h2>
            <div className="mt-3 flex flex-wrap items-end gap-2">
              <input
                className="w-36 rounded border px-3 py-2 text-sm"
                placeholder="KCD 키"
                value={mapKcd}
                onChange={(e) => setMapKcd(e.target.value)}
              />
              <input
                className="min-w-[10rem] flex-1 rounded border px-3 py-2 text-sm"
                placeholder="통합 검색"
                value={mapQ}
                onChange={(e) => setMapQ(e.target.value)}
              />
              <button
                type="button"
                className="rounded bg-brand-navy px-4 py-2 text-sm text-white"
                onClick={() => {
                  setMapPage(1)
                  setTimeout(() => loadMap(), 0)
                }}
              >
                검색
              </button>
              <button type="button" className="rounded border px-3 py-2 text-sm" onClick={() => downloadFromApi('/operator/design-support/admin/kcd-rider-mappings/template', 'kcd_rider_mapping_template.csv')}>
                템플릿
              </button>
              <button type="button" className="rounded border px-3 py-2 text-sm" onClick={() => downloadFromApi('/operator/design-support/admin/kcd-rider-mappings/export', 'kcd_rider_mapping.csv')}>
               CSV 내려받기
              </button>
              <label className="rounded border px-3 py-2 text-sm cursor-pointer">
                가져오기
                <input type="file" accept=".csv" className="hidden" onChange={(e) => upload('/operator/design-support/admin/kcd-rider-mappings/import', e.target.files?.[0], e.target)} />
              </label>
            </div>
            <p className="mt-1 text-xs text-slate-500">전체 {mapTotal.toLocaleString()}건</p>
            <div className="mt-2 overflow-x-auto rounded border">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-xs font-semibold text-slate-600">
                  <tr>
                    <th className="px-3 py-2">KCD</th>
                    <th className="px-3 py-2">특약코드</th>
                    <th className="px-3 py-2">특약명</th>
                    <th className="px-3 py-2">매칭</th>
                    <th className="px-3 py-2">비고</th>
                  </tr>
                </thead>
                <tbody>
                  {mapLoading ? (
                    <tr>
                      <td colSpan={5} className="py-6 text-center text-slate-500">
                        불러오는 중…
                      </td>
                    </tr>
                  ) : mapRows.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="py-8 text-center text-slate-500">
                        조건에 맞는 매핑이 없습니다. KCD 키·검색어를 바꾸거나 CSV를 적재하세요.
                      </td>
                    </tr>
                  ) : (
                    mapRows.map((r) => (
                      <tr key={r.id} className="border-t">
                        <td className="px-3 py-2 font-mono text-xs">{r.kcdCategory}</td>
                        <td className="px-3 py-2 font-mono text-xs">{r.riderTypeCode}</td>
                        <td className="px-3 py-2">{r.riderTypeName}</td>
                        <td className="px-3 py-2 text-xs">{r.matchType || '—'}</td>
                        <td className="px-3 py-2 text-xs">{r.notes || '—'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {mapPages > 1 && (
              <div className="mt-3 flex gap-2 text-sm">
                <button type="button" className="rounded border px-2 py-1 disabled:opacity-40" disabled={mapPage <= 1} onClick={() => setMapPage((p) => p - 1)}>
                  이전
                </button>
                <span className="self-center text-xs text-slate-600">
                  {mapPage} / {mapPages}
                </span>
                <button type="button" className="rounded border px-2 py-1 disabled:opacity-40" disabled={mapPage >= mapPages} onClick={() => setMapPage((p) => p + 1)}>
                  다음
                </button>
              </div>
            )}
          </section>
        </div>
      )}

      {tab === 'surgery' && (
        <div className="mt-6 space-y-4">
          <div className="flex flex-wrap items-end gap-2">
            <input
              className="min-w-[12rem] flex-1 rounded border px-3 py-2 text-sm"
              placeholder="수술명 검색"
              value={sgQ}
              onChange={(e) => setSgQ(e.target.value)}
            />
            <button
              type="button"
              className="rounded bg-brand-navy px-4 py-2 text-sm text-white"
              onClick={() => {
                setSgPage(1)
                setTimeout(() => loadSg(), 0)
              }}
            >
              검색
            </button>
            <button type="button" className="rounded border px-3 py-2 text-sm" onClick={() => downloadFromApi('/operator/design-support/admin/surgery-grades/template', 'surgery_grades_template.csv')}>
              템플릿
            </button>
            <button type="button" className="rounded border px-3 py-2 text-sm" onClick={() => downloadFromApi('/operator/design-support/admin/surgery-grades/export', 'surgery_grades.csv')}>
             CSV 내려받기
            </button>
            <label className="rounded border px-3 py-2 text-sm cursor-pointer">
              가져오기
              <input type="file" accept=".csv" className="hidden" onChange={(e) => upload('/operator/design-support/admin/surgery-grades/import', e.target.files?.[0], e.target)} />
            </label>
          </div>
          <p className="text-xs text-slate-500">전체 {sgTotal.toLocaleString()}건 · 페이지 {sgPage} / {sgPages}</p>
          <div className="overflow-x-auto rounded border">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-xs font-semibold text-slate-600">
                <tr>
                  <th className="px-3 py-2">수술명</th>
                  <th className="px-3 py-2">1~3</th>
                  <th className="px-3 py-2">1~5</th>
                  <th className="px-3 py-2">1~7</th>
                  <th className="px-3 py-2">1~8</th>
                  <th className="px-3 py-2">출처</th>
                </tr>
              </thead>
              <tbody>
                {sgLoading ? (
                  <tr>
                    <td colSpan={6} className="py-6 text-center text-slate-500">
                      불러오는 중…
                    </td>
                  </tr>
                ) : sgRows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-slate-500">
                      데이터가 없습니다.
                    </td>
                  </tr>
                ) : (
                  sgRows.map((r) => (
                    <tr key={r.id} className="border-t">
                      <td className="px-3 py-2">{r.surgeryName}</td>
                      <td className="px-3 py-2 text-xs">{r.grade3 || '—'}</td>
                      <td className="px-3 py-2 text-xs">{r.grade5 || '—'}</td>
                      <td className="px-3 py-2 text-xs">{r.grade7 || '—'}</td>
                      <td className="px-3 py-2 text-xs">{r.grade8 || '—'}</td>
                      <td className="px-3 py-2 text-xs">{r.dataSourceNote || '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {sgPages > 1 && (
            <div className="flex gap-2 text-sm">
              <button type="button" className="rounded border px-2 py-1 disabled:opacity-40" disabled={sgPage <= 1} onClick={() => setSgPage((p) => p - 1)}>
                이전
              </button>
              <button type="button" className="rounded border px-2 py-1 disabled:opacity-40" disabled={sgPage >= sgPages} onClick={() => setSgPage((p) => p + 1)}>
                다음
              </button>
            </div>
          )}
        </div>
      )}

      {tab === 'tierSnapshot' && (
        <div className="mt-6 space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <label className="text-xs font-medium text-slate-600">원수사 코드</label>
              <input
                className="mt-0.5 w-full rounded border border-slate-300 px-3 py-2 text-sm"
                value={stInsurerCode}
                onChange={(e) => setStInsurerCode(e.target.value)}
                placeholder="필터 (비우면 전체)"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">상품 코드</label>
              <input
                className="mt-0.5 w-full rounded border border-slate-300 px-3 py-2 text-sm"
                value={stProductCode}
                onChange={(e) => setStProductCode(e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">종 체계</label>
              <select
                className="mt-0.5 w-full rounded border border-slate-300 px-3 py-2 text-sm"
                value={stTierSystem}
                onChange={(e) => setStTierSystem(e.target.value)}
              >
                <option value="">전체</option>
                <option value="3">3</option>
                <option value="5">5</option>
                <option value="7">7</option>
                <option value="8">8</option>
              </select>
            </div>
            <div className="sm:col-span-2 lg:col-span-4">
              <label className="text-xs font-medium text-slate-600">검색 (수술명·원수사)</label>
              <input
                className="mt-0.5 w-full rounded border border-slate-300 px-3 py-2 text-sm"
                value={stQ}
                onChange={(e) => setStQ(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    setStPage(1)
                    setTimeout(() => loadTierSnapshot(), 0)
                  }
                }}
              />
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <button
              type="button"
              className="rounded bg-brand-navy px-4 py-2 text-sm text-white hover:bg-slate-800"
              onClick={() => {
                setStPage(1)
                setTimeout(() => loadTierSnapshot(), 0)
              }}
            >
              검색
            </button>
            <button
              type="button"
              className="rounded border border-slate-300 px-3 py-2 text-sm hover:bg-slate-50"
              onClick={() => downloadFromApi('/operator/design-support/admin/surgery-tier-snapshots/template', 'surgery_tier_snapshot_template.csv')}
            >
              템플릿
            </button>
            <button
              type="button"
              className="rounded border border-slate-300 px-3 py-2 text-sm hover:bg-slate-50"
              onClick={() => downloadFromApi('/operator/design-support/admin/surgery-tier-snapshots/export', 'surgery_tier_snapshot.csv')}
            >
              CSV 내려받기
            </button>
            <label className="rounded border border-slate-300 px-3 py-2 text-sm hover:bg-slate-50 cursor-pointer">
              가져오기
              <input
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => upload('/operator/design-support/admin/surgery-tier-snapshots/import', e.target.files?.[0], e.target)}
              />
            </label>
          </div>
          <p className="text-xs text-slate-500">
            전체 {stTotal.toLocaleString()}건 · 페이지 {stPage} / {stPages}
          </p>
          <div className="overflow-x-auto rounded border border-slate-200">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs font-semibold text-slate-600">
                <tr>
                  <th className="px-3 py-2 whitespace-nowrap">원수사</th>
                  <th className="px-3 py-2">상품</th>
                  <th className="px-3 py-2">체계</th>
                  <th className="px-3 py-2 min-w-[10rem]">수술명</th>
                  <th className="px-3 py-2">라벨</th>
                  <th className="px-3 py-2 whitespace-nowrap">시작</th>
                  <th className="px-3 py-2 whitespace-nowrap">종료</th>
                  <th className="px-3 py-2">공시</th>
                  <th className="px-3 py-2">약관 URL</th>
                  <th className="px-3 py-2 min-w-[8rem]">메모</th>
                </tr>
              </thead>
              <tbody>
                {stLoading ? (
                  <tr>
                    <td colSpan={10} className="px-3 py-8 text-center text-slate-500">
                      불러오는 중…
                    </td>
                  </tr>
                ) : stRows.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="px-3 py-8 text-center text-slate-500">
                      데이터가 없습니다. 템플릿으로 CSV를 채운 뒤 가져오기 하세요.
                    </td>
                  </tr>
                ) : (
                  stRows.map((r) => (
                    <tr key={r.id} className="border-t border-slate-100">
                      <td className="px-3 py-2 text-xs">
                        <span className="font-mono">{r.insurerCode}</span>
                        <span className="block text-slate-500">{r.insurerName || '—'}</span>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{r.productCode}</td>
                      <td className="px-3 py-2">{r.tierSystem}</td>
                      <td className="px-3 py-2">{r.surgeryName}</td>
                      <td className="px-3 py-2 text-xs">{r.tierLabel || '—'}</td>
                      <td className="px-3 py-2 text-xs whitespace-nowrap">{r.effectiveFrom || '—'}</td>
                      <td className="px-3 py-2 text-xs whitespace-nowrap">{r.effectiveTo || '—'}</td>
                      <td className="px-3 py-2 text-xs">
                        {r.disclosureUrl ? (
                          <a className="text-brand-sky underline" href={r.disclosureUrl} target="_blank" rel="noreferrer noopener">
                            링크
                          </a>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs max-w-[10rem] truncate" title={r.termsUrl || ''}>
                        {r.termsUrl ? (
                          <a className="text-brand-sky underline" href={r.termsUrl} target="_blank" rel="noreferrer noopener">
                            링크
                          </a>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-600 max-w-[12rem] truncate" title={r.sourceNote || ''}>
                        {r.sourceNote || '—'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {stPages > 1 && (
            <div className="flex gap-2 text-sm">
              <button type="button" className="rounded border px-2 py-1 disabled:opacity-40" disabled={stPage <= 1} onClick={() => setStPage((p) => p - 1)}>
                이전
              </button>
              <button type="button" className="rounded border px-2 py-1 disabled:opacity-40" disabled={stPage >= stPages} onClick={() => setStPage((p) => p + 1)}>
                다음
              </button>
            </div>
          )}
        </div>
      )}

      {tab === 'majorN' && (
        <div className="mt-6 space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <label className="text-xs font-medium text-slate-600">원수사 코드</label>
              <input
                className="mt-0.5 w-full rounded border border-slate-300 px-3 py-2 text-sm"
                value={mdInsurerCode}
                onChange={(e) => setMdInsurerCode(e.target.value)}
                placeholder="필터 (비우면 전체)"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">상품 코드</label>
              <input
                className="mt-0.5 w-full rounded border border-slate-300 px-3 py-2 text-sm"
                value={mdProductCode}
                onChange={(e) => setMdProductCode(e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">N 라벨</label>
              <input
                className="mt-0.5 w-full rounded border border-slate-300 px-3 py-2 text-sm"
                value={mdNLabel}
                onChange={(e) => setMdNLabel(e.target.value)}
                placeholder="예: 3대"
              />
            </div>
            <div className="sm:col-span-2 lg:col-span-4">
              <label className="text-xs font-medium text-slate-600">검색 (KCD·질병명·원수사)</label>
              <input
                className="mt-0.5 w-full rounded border border-slate-300 px-3 py-2 text-sm"
                value={mdQ}
                onChange={(e) => setMdQ(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    setMdPage(1)
                    setTimeout(() => loadMajorN(), 0)
                  }
                }}
              />
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <button
              type="button"
              className="rounded bg-brand-navy px-4 py-2 text-sm text-white hover:bg-slate-800"
              onClick={() => {
                setMdPage(1)
                setTimeout(() => loadMajorN(), 0)
              }}
            >
              검색
            </button>
            <button
              type="button"
              className="rounded border border-slate-300 px-3 py-2 text-sm hover:bg-slate-50"
              onClick={() => downloadFromApi('/operator/design-support/admin/major-disease-n/template', 'major_disease_n_template.csv')}
            >
              템플릿
            </button>
            <button
              type="button"
              className="rounded border border-slate-300 px-3 py-2 text-sm hover:bg-slate-50"
              onClick={() => downloadFromApi('/operator/design-support/admin/major-disease-n/export', 'major_disease_n.csv')}
            >
              CSV 내려받기
            </button>
            <label className="rounded border border-slate-300 px-3 py-2 text-sm hover:bg-slate-50 cursor-pointer">
              가져오기
              <input
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => upload('/operator/design-support/admin/major-disease-n/import', e.target.files?.[0], e.target)}
              />
            </label>
          </div>
          <p className="text-xs text-slate-500">
            전체 {mdTotal.toLocaleString()}건 · 페이지 {mdPage} / {mdPages}
          </p>
          <div className="overflow-x-auto rounded border border-slate-200">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs font-semibold text-slate-600">
                <tr>
                  <th className="px-3 py-2 whitespace-nowrap">원수사</th>
                  <th className="px-3 py-2">상품</th>
                  <th className="px-3 py-2">N</th>
                  <th className="px-3 py-2">KCD</th>
                  <th className="px-3 py-2 min-w-[10rem]">질병명</th>
                  <th className="px-3 py-2 whitespace-nowrap">시작</th>
                  <th className="px-3 py-2 whitespace-nowrap">종료</th>
                  <th className="px-3 py-2">공시</th>
                  <th className="px-3 py-2 min-w-[8rem]">메모</th>
                </tr>
              </thead>
              <tbody>
                {mdLoading ? (
                  <tr>
                    <td colSpan={9} className="px-3 py-8 text-center text-slate-500">
                      불러오는 중…
                    </td>
                  </tr>
                ) : mdRows.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-3 py-8 text-center text-slate-500">
                      데이터가 없습니다. 템플릿으로 CSV를 채운 뒤 가져오기 하세요.
                    </td>
                  </tr>
                ) : (
                  mdRows.map((r) => (
                    <tr key={r.id} className="border-t border-slate-100">
                      <td className="px-3 py-2 text-xs">
                        <span className="font-mono">{r.insurerCode}</span>
                        <span className="block text-slate-500">{r.insurerName || '—'}</span>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{r.productCode}</td>
                      <td className="px-3 py-2 text-xs">{r.nLabel || '—'}</td>
                      <td className="px-3 py-2 font-mono text-xs">{r.kcdCategory || '—'}</td>
                      <td className="px-3 py-2">{r.diseaseNameKo || '—'}</td>
                      <td className="px-3 py-2 text-xs whitespace-nowrap">{r.effectiveFrom || '—'}</td>
                      <td className="px-3 py-2 text-xs whitespace-nowrap">{r.effectiveTo || '—'}</td>
                      <td className="px-3 py-2 text-xs">
                        {r.disclosureUrl ? (
                          <a className="text-brand-sky underline" href={r.disclosureUrl} target="_blank" rel="noreferrer noopener">
                            링크
                          </a>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-600 max-w-[12rem] truncate" title={r.sourceNote || ''}>
                        {r.sourceNote || '—'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {mdPages > 1 && (
            <div className="flex gap-2 text-sm">
              <button type="button" className="rounded border px-2 py-1 disabled:opacity-40" disabled={mdPage <= 1} onClick={() => setMdPage((p) => p - 1)}>
                이전
              </button>
              <button type="button" className="rounded border px-2 py-1 disabled:opacity-40" disabled={mdPage >= mdPages} onClick={() => setMdPage((p) => p + 1)}>
                다음
              </button>
            </div>
          )}
        </div>
      )}
      </div>
    </DesignSupportLayout>
  )
}
